/* eslint-disable @typescript-eslint/no-unused-vars */
import { useCallback, useEffect, useState } from 'react';

import { generateDeviceId } from '@1debit/device-intelligence';
import { gql, useApolloClient } from '@apollo/client';
import { datadogRum } from '@datadog/browser-rum';
import { useMachine } from '@xstate/react';
import cloneDeep from 'lodash/cloneDeep';
import { requestStateMachine } from 'machines/requestStateMachine';
import getConfig from 'next/config';
import { useRouter } from 'next/router';

import useEncryptionKeys from 'components/SubPages/ClaimInfo/features/greendotIntegration/useEncryptionKeys';
import useArkose from 'components/SubPages/ClaimInfo/useArkose';
import { encryptDebitCard } from 'components/SubPages/ClaimInfo/utils/encryption';

import { useGqlMutation } from 'hooks/useGQLMutation';
import { fetcher } from 'hooks/useSWR';
import { useUserContext } from 'hooks/useUserContext';

import { deviceIntelligenceEnvironment } from 'utils/device-intelligence/deviceIntelligenceUtils';
import {
  getAnonymousId,
  handleTrackingInputs,
  InputFieldTrackingFunction,
  trackCTATapped,
  trackFieldFilled,
  trackPayAnyoneReceiverError,
  trackSuccessfulOcrScan,
} from 'utils/segment/trackingEvents';

import { useNuData } from './utils/nudata/useNudata';
import pollTransactionStatus, { PollingResult } from './pollTransactionStatus';

export const PAY_ANYONE_CLAIM_NOT_SAVED_DEBIT_CARD = gql`
  mutation PayAnyoneClaimNotSavedDebitResponse($input: ClaimPayAnyoneInput!) {
    unauthenticated {
      p2p {
        claim_pay_anyone(input: $input) {
          __typename
          ... on PayAnyoneClaimError {
            error_id
            error_body
            error_header
          }
          ... on ClaimPayAnyoneResult {
            success
            token
          }
        }
      }
    }
  }
`;

const logErrorWithToken = (
  token: string,
  publicId: string,
  message: string,
  ...rest
) => {
  const error = new Error(message);
  window.Rollbar.error(
    message,
    {
      token,
      publicId,
    },
    ...rest
  );
  datadogRum.addError(error, {
    token,
    publicId,
    ...rest,
  });
};

const obfuscatedFieldValue = 'xxxxxxxx';

const getFirstSixDigits = (cardNumber: string) => {
  const cardNumberNoSpaces = cardNumber.replace(/\s/g, '');
  return cardNumberNoSpaces.length >= 16
    ? cardNumberNoSpaces.substring(0, 6)
    : obfuscatedFieldValue;
};

type useCreateClaimInfoFormHookApi = {
  isLoading: boolean;
  hasError: boolean;
  submitForm: (any) => Promise<void>;
  dismissError: () => void;
  trackCardNumber: InputFieldTrackingFunction;
  trackExpDate: InputFieldTrackingFunction;
  trackCVV: InputFieldTrackingFunction;
  trackZipCode: InputFieldTrackingFunction;
  trackFirstName: InputFieldTrackingFunction;
  trackLastName: InputFieldTrackingFunction;
  trackEmail: InputFieldTrackingFunction;
  error?: Error;
  errorMessage?: string;
  errorId?: string;
  trackOnSuccessfulOcrScan: (isPartialScan: boolean) => void;
  trackOnOcrButtonClick: () => void;
  trackCardNumberOcr: (cardNumber: string) => void;
};

function useCreateClaimInfoFormHook(
  postUrl: string,
  analyticEventPrefix: string,
  onSubmitSuccess: (any) => void,
  pollingUrl?: string,
  pollingFunction?: (result: any) => PollingResult,
  setChallengeContext?: (challengeContext: string) => void
): useCreateClaimInfoFormHookApi {
  const {
    publicRuntimeConfig: { socureDeviceSdkKey, deployEnvironment },
  } = getConfig();
  const apolloClient = useApolloClient();
  const [userData] = useUserContext();
  const router = useRouter();
  const [requestState, requestSend] = useMachine(requestStateMachine);
  const [errorId, setErrorId] = useState(null);
  const [deviceSessionId, setDeviceSessionId] = useState(null);
  const { keys, error: encryptionKeysError } = useEncryptionKeys();

  const publicId = userData?.publicId || null;
  const bank = userData?.bank;

  const useGqlGetAndClaimPayAnyone =
    userData?.flipperFlags?.graphqlUsage?.useGqlGetAndClaimPayAnyone;
  const token = router.query.token as string;
  const pageLocation = `${analyticEventPrefix} Claim Info Page`;
  const uniqueIdPrefix = analyticEventPrefix.toLowerCase().replace(/\s/i, '_');

  const not_saved_debit_card = useGqlMutation(
    PAY_ANYONE_CLAIM_NOT_SAVED_DEBIT_CARD
  );
  const {
    execute,
    // loading: graphqlloading,
    // error: graphqlerror,
    // data: graphqlData,
  } = not_saved_debit_card;

  const { runArkose } = useArkose();

  const {
    beginBehaviouralMonitoring,
    stopBehaviouralMonitoring,
    getBehaviouralData,
    getSessionId,
  } = useNuData();

  beginBehaviouralMonitoring();

  if (encryptionKeysError) {
    datadogRum.addError(new Error('Encryption Key Fetch Error'), {
      publicId,
      message: encryptionKeysError,
    });
    requestSend('ERROR');
  }

  useEffect(() => {
    const deviceOptions = {
      publicKey: socureDeviceSdkKey,
      endpoint: 'https://transaction.chime.com/api/ingestion',
      userConsent: true,
      context: 'login',
    };

    if (!window.devicer) {
      logErrorWithToken(
        token,
        publicId,

        'Socure devicer failed to load.'
      );
      requestSend('ERROR');
    } else {
      try {
        window.devicer.run(deviceOptions, response => {
          if (!response) {
            logErrorWithToken(
              token,
              publicId,
              'Socure devicer did not respond.'
            );

            requestSend('ERROR');
            return;
          }

          if (!response.sessionId) {
            logErrorWithToken(
              token,
              publicId,
              'Socure devicer did not respond with a sessionId.',
              response
            );

            requestSend('ERROR');
            return;
          }
          setDeviceSessionId(response.sessionId);
        });
      } catch (error) {
        logErrorWithToken(
          token,
          publicId,
          'Socure devicer.run call failed',
          error
        );
        requestSend('ERROR');
      }
    }
  }, [publicId, socureDeviceSdkKey, router, token, requestSend]);

  const submitForm = async ({ cardnumber, expdate, csc, ...data }) => {
    const bin = cardnumber.replace(/\s/g, '').slice(0, 6);
    const cardLastFour = cardnumber.replace(/\s/g, '').slice(-4);

    try {
      trackCTATapped(
        analyticEventPrefix,
        'Transfer',
        pageLocation,
        `${uniqueIdPrefix}_card_info_transfer_button`,
        publicId,
        null
      );

      requestSend('START');

      let arkoseToken = '';
      try {
        arkoseToken = await runArkose();
      } catch (error) {
        datadogRum.addError(error, {
          token,
          publicId,
        });
      }

      stopBehaviouralMonitoring();
      const ndsBehaviouralData = getBehaviouralData();
      const ndsSessionId = getSessionId();
      const deviceId = generateDeviceId({
        env: deviceIntelligenceEnvironment(deployEnvironment),
      });

      const basePostBody = {
        first_name: data.firstName,
        last_name: data.lastName,
        email: data.email,
        zip_code: data.postalCode,
        bin,
        last_four: cardLastFour,
        expiration: formatExpirationDate(expdate),
        device_id: deviceSessionId,
        segment_anonymous_id: getAnonymousId(),
        reuse_external_card: data.cardSave,
        arkose_token: arkoseToken,
        nds: ndsBehaviouralData,
        nudata_session_id: ndsSessionId,
        token,
      };

      const pfPath = postUrl?.includes(`/pf/`);
      if (useGqlGetAndClaimPayAnyone && pfPath) {
        const baseConfig: any = cloneDeep(basePostBody);
        delete baseConfig?.segment_anonymous_id;
        delete baseConfig?.pay_anyone_tokens;
        const input = {
          ...baseConfig,
          encrypted_parts: encryptDebitCard(keys, cardnumber, expdate, csc),
        };
        const result = await execute(
          {
            input,
          },
          {
            headers: {
              ...(data.challengeResponseContext && {
                'Chime-Challenge-Context': data.challengeResponseContext,
              }),
            },
          }
        );
        const { data: claimData, errors: claimError } = result || {};
        const claimDataResponse =
          claimData?.unauthenticated?.p2p?.claim_pay_anyone;
        if (claimDataResponse?.__typename === 'PayAnyoneClaimError') {
          throw claimDataResponse;
        } else if (claimError) {
          throw claimError;
        }
      } else {
        await fetcher(postUrl, {
          method: 'POST',
          json: {
            ...basePostBody,
            encrypted_parts: encryptDebitCard(keys, cardnumber, expdate, csc),
          },
          headers: {
            ...(data.challengeResponseContext && {
              'Chime-Challenge-Context': data.challengeResponseContext,
            }),
          },
        });
      }

      if (pollingUrl && pollingFunction) {
        await pollTransactionStatus(pollingUrl, pollingFunction, {
          token,
          apolloClient,
          useGqlGetAndClaimPayAnyone,
        });
      }

      requestSend('RESUME');

      onSubmitSuccess({ cardnumber, csc, expdate, ...data });
    } catch (e) {
      if (e.challenge_context) {
        setChallengeContext(e.challenge_context);
        return;
      }

      if (e.error_body) {
        trackPayAnyoneReceiverError('Transfer', e.error_body, publicId);
        requestSend('ERROR', { error: e });
        return;
      }

      if (e.error_header) {
        trackPayAnyoneReceiverError('Transfer', e.error_header, publicId);
        requestSend('ERROR', { errorMessage: e.error_header });
        setErrorId('debit_card_not_eligible');
        return;
      }

      trackPayAnyoneReceiverError('Transfer', 'Something went wrong', publicId);
      requestSend('ERROR');
    }
  };

  const dismissError = () => {
    requestSend('RESUME');
  };

  const trackCardNumber = handleTrackingInputs(
    (event: any, publicId: string) => {
      const firstSixDigits = getFirstSixDigits(event.target?.value);

      trackFieldFilled(
        analyticEventPrefix,
        'Card Number',
        pageLocation,
        firstSixDigits,
        publicId
      );
    }
  );

  const trackCardNumberOcr = (cardNumber: string) => {
    const firstSixDigits = getFirstSixDigits(cardNumber);

    trackFieldFilled(
      analyticEventPrefix,
      'Card Number OCR',
      pageLocation,
      firstSixDigits,
      publicId
    );
  };

  const trackExpDate = handleTrackingInputs((event: any, publicId: string) => {
    trackFieldFilled(
      analyticEventPrefix,
      'Expiration Date',
      pageLocation,
      obfuscatedFieldValue,
      publicId
    );
  });

  const trackCVV = handleTrackingInputs((event: any, publicId: string) => {
    trackFieldFilled(
      analyticEventPrefix,
      'CVV',
      pageLocation,
      obfuscatedFieldValue,
      publicId
    );
  });

  const trackZipCode = handleTrackingInputs((event: any, publicId: string) => {
    trackFieldFilled(
      analyticEventPrefix,
      'Zip Code',
      pageLocation,
      event.target.value,
      publicId
    );
  });

  const trackFirstName = useCallback(
    (event: any, publicId: string) => {
      const trackingFunction = handleTrackingInputs(
        (event: any, publicId: string) => {
          trackFieldFilled(
            analyticEventPrefix,
            'First Name',
            pageLocation,
            event.target.value,
            publicId
          );
        }
      );
      trackingFunction(event, publicId);
    },
    [analyticEventPrefix, pageLocation]
  );

  const trackLastName = useCallback(
    (event: any, publicId: string) => {
      const trackingFunction = handleTrackingInputs(
        (event: any, publicId: string) => {
          trackFieldFilled(
            analyticEventPrefix,
            'Last Name',
            pageLocation,
            event.target.value,
            publicId
          );
        }
      );
      trackingFunction(event, publicId);
    },
    [analyticEventPrefix, pageLocation]
  );

  const trackEmail = handleTrackingInputs((event: any, publicId: string) => {
    trackFieldFilled(
      analyticEventPrefix,
      'Email',
      pageLocation,
      event.target.value,
      publicId
    );
  });

  const trackOnOcrButtonClick = () => {
    trackCTATapped(
      analyticEventPrefix,
      'OCR Camera Button',
      pageLocation,
      `${uniqueIdPrefix}_ocr_camera_button`,
      publicId
    );
  };

  const trackOnSuccessfulOcrScan = isPartialScan => {
    trackSuccessfulOcrScan(publicId, isPartialScan);
  };

  const formatExpirationDate = (expDate: string): string => {
    const [month, last2digitYear] = expDate.split('/');
    return `${month}/20${last2digitYear}`;
  };

  return {
    submitForm,
    isLoading: requestState.value === 'loading',
    hasError: requestState.value === 'error',
    errorMessage: requestState.context?.errorMessage,
    error: requestState.context?.error,
    errorId,
    dismissError,
    trackCardNumber,
    trackExpDate,
    trackCVV,
    trackZipCode,
    trackFirstName,
    trackLastName,
    trackEmail,
    trackOnSuccessfulOcrScan,
    trackOnOcrButtonClick,
    trackCardNumberOcr,
  };
}

export default useCreateClaimInfoFormHook;
